import { scopedProjectKey, scopeProjectRef } from "@cmmd-forge/client-runtime";
import {
  DEFAULT_RUNTIME_MODE,
  type CmmdAnchorKind,
  type LinkedPullRequest,
  type ScopedProjectRef,
} from "@cmmd-forge/contracts";
import { useParams, useRouter } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  type DraftThreadEnvMode,
  type DraftThreadState,
  useComposerDraftStore,
} from "../composerDraftStore";
import type { DraftId } from "../draftId";
import { newDraftId, newThreadId } from "../lib/utils";
import { orderItemsByPreferredIds } from "../components/Sidebar.logic";
import { canAdoptCurrentDraft } from "./adoptCurrentDraft";
import { openDraftRoute } from "./openDraftRoute";
import { useProjectsAcrossEnvironments } from "./useProjectsAcrossEnvironments";
import { usePrimaryEnvironmentId } from "../environments/primary";
import { useSettings } from "../hooks/useSettings";
import { deriveLogicalProjectKey } from "../logicalProject";
import { useStore } from "../store";
import { createThreadSelectorByRef } from "../storeSelectors";
import { resolveThreadRouteTarget } from "../threadRoutes";
import { useUiStateStore } from "../uiStateStore";
import { resolveNewThreadRuntimeProjectRef } from "../lib/threadRuntimeTarget";
import { seedThreadComposerContext } from "../lib/threadComposerBootstrap";
import {
  buildDraftContextPatch,
  buildNewDraftRecord,
  buildDraftThreadContextPatch,
  mentionsDraftContext,
} from "./newThreadDraftContext";

type DraftReuse = "allow" | "never";

function useNewThreadState() {
  const projects = useProjectsAcrossEnvironments();
  const router = useRouter();
  const getCurrentRouteTarget = useCallback(() => {
    const currentRouteParams = router.state.matches[router.state.matches.length - 1]?.params ?? {};
    return resolveThreadRouteTarget(currentRouteParams);
  }, [router]);

  return useCallback(
    (
      projectRef: ScopedProjectRef,
      options?: {
        branch?: string | null;
        worktreePath?: string | null;
        envMode?: DraftThreadEnvMode;
        anchorKind?: CmmdAnchorKind | null;
        anchorCmmdId?: string | null;
        cmmdTaskId?: string | null;
        linkedPullRequest?: LinkedPullRequest | null;
        openBrowserPreview?: boolean;
        initialPrompt?: string;
        autoSubmit?: boolean;
        draftReuse?: DraftReuse;
        /**
         * Morph the composer across the route change instead of swapping it.
         *
         * Only the new-thread page sets this: it is the one entry point where a
         * composer is already on screen in a different position, so it is the
         * one place the morph describes something real. Sidebar/palette entries
         * open a draft from elsewhere and should not animate a box that was
         * never visible.
         */
        viewTransition?: boolean;
      },
    ): Promise<NewThreadLaunchResult> => {
      const {
        getDraftSessionByLogicalProjectKey,
        getDraftSession,
        getDraftThread,
        applyStickyState,
        setDraftThreadContext,
        setLogicalProjectDraftThreadId,
      } = useComposerDraftStore.getState();
      const currentRouteTarget = getCurrentRouteTarget();
      const project = projects.find(
        (candidate) =>
          candidate.id === projectRef.projectId &&
          candidate.environmentId === projectRef.environmentId,
      );
      const logicalProjectKey = project
        ? deriveLogicalProjectKey(project)
        : scopedProjectKey(projectRef);
      const contextPatch = buildDraftContextPatch(options);
      const threadContextPatch = buildDraftThreadContextPatch(options);
      const mentionsContext = mentionsDraftContext(options);
      const storedDraftThread = getDraftSessionByLogicalProjectKey(logicalProjectKey);
      const latestActiveDraftThread = readDraftForRouteTarget(currentRouteTarget, {
        getDraftThread,
        getDraftSession,
      });
      const applyInitialPrompt = (draftId: Parameters<typeof setDraftThreadContext>[0]) => {
        const draftStore = useComposerDraftStore.getState();
        seedThreadComposerContext({
          target: draftId,
          prompt: options?.initialPrompt,
          ...(options?.autoSubmit !== undefined ? { autoSubmit: options.autoSubmit } : {}),
          setPrompt: draftStore.setPrompt,
          setPendingAutoSubmit: draftStore.setPendingAutoSubmit,
        });
      };

      const reusableStoredDraft = draftCandidateForReuse(storedDraftThread, options?.draftReuse);
      if (reusableStoredDraft) {
        return (async () => {
          applyMentionedDraftContext({
            mentionsContext,
            draftId: reusableStoredDraft.draftId,
            contextPatch,
            setDraftThreadContext,
          });
          setLogicalProjectDraftThreadId(
            logicalProjectKey,
            projectRef,
            reusableStoredDraft.draftId,
            {
              threadId: reusableStoredDraft.threadId,
              ...optionalCmmdTaskId(options?.cmmdTaskId),
            },
          );
          applyInitialPrompt(reusableStoredDraft.draftId);
          await openDraftRoute({
            router,
            draftId: reusableStoredDraft.draftId,
            openBrowserPreview: options?.openBrowserPreview,
            viewTransition: options?.viewTransition,
          });
          return toNewThreadLaunchResult(reusableStoredDraft);
        })();
      }

      const reusableActiveDraft = draftCandidateForReuse(
        latestActiveDraftThread,
        options?.draftReuse,
      );
      if (
        reusableActiveDraft &&
        currentRouteTarget?.kind === "draft" &&
        canAdoptCurrentDraft({
          latestActiveDraft: reusableActiveDraft,
          routeTargetKind: currentRouteTarget.kind,
          logicalProjectKey,
        })
      ) {
        applyMentionedDraftContext({
          mentionsContext,
          draftId: currentRouteTarget.draftId,
          contextPatch,
          setDraftThreadContext,
        });
        setLogicalProjectDraftThreadId(logicalProjectKey, projectRef, currentRouteTarget.draftId, {
          threadId: reusableActiveDraft.threadId,
          createdAt: reusableActiveDraft.createdAt,
          runtimeMode: reusableActiveDraft.runtimeMode,
          interactionMode: reusableActiveDraft.interactionMode,
          ...threadContextPatch,
        });
        applyInitialPrompt(currentRouteTarget.draftId);
        return Promise.resolve({
          draftId: currentRouteTarget.draftId,
          threadId: reusableActiveDraft.threadId,
          projectRef,
        });
      }

      const draftId = newDraftId();
      const threadId = newThreadId();
      const createdAt = new Date().toISOString();
      return (async () => {
        setLogicalProjectDraftThreadId(
          logicalProjectKey,
          projectRef,
          draftId,
          buildNewDraftRecord(options, {
            threadId,
            createdAt,
            runtimeMode: DEFAULT_RUNTIME_MODE,
          }),
        );
        applyStickyState(draftId);
        applyInitialPrompt(draftId);

        await openDraftRoute({
          router,
          draftId,
          openBrowserPreview: options?.openBrowserPreview,
          viewTransition: options?.viewTransition,
        });
        return { draftId, threadId, projectRef };
      })();
    },
    [getCurrentRouteTarget, router, projects],
  );
}

interface NewThreadLaunchResult {
  readonly draftId: DraftId;
  readonly threadId: DraftThreadState["threadId"];
  readonly projectRef: ScopedProjectRef;
}

function draftCandidateForReuse<T>(candidate: T | null, reuse: DraftReuse | undefined): T | null {
  return reuse === "never" ? null : candidate;
}

function applyMentionedDraftContext(input: {
  readonly mentionsContext: boolean;
  readonly draftId: DraftId;
  readonly contextPatch: ReturnType<typeof buildDraftContextPatch>;
  readonly setDraftThreadContext: ReturnType<
    typeof useComposerDraftStore.getState
  >["setDraftThreadContext"];
}): void {
  if (input.mentionsContext) {
    input.setDraftThreadContext(input.draftId, input.contextPatch);
  }
}

function optionalCmmdTaskId(cmmdTaskId: string | null | undefined): {
  readonly cmmdTaskId?: string | null;
} {
  return cmmdTaskId === undefined ? {} : { cmmdTaskId };
}

function toNewThreadLaunchResult(thread: DraftThreadState & { readonly draftId: DraftId }) {
  return {
    draftId: thread.draftId,
    threadId: thread.threadId,
    projectRef: scopeProjectRef(thread.environmentId, thread.projectId),
  };
}

export function useNewThreadHandler() {
  const openNewThread = useNewThreadState();
  const handleNewThread = useCallback(
    async (...args: Parameters<typeof openNewThread>) => {
      await openNewThread(...args);
    },
    [openNewThread],
  );

  return {
    handleNewThread,
    openNewThread,
  };
}

export function useHandleNewThread() {
  const projectOrder = useUiStateStore((store) => store.projectOrder);
  const routeTarget = useParams({
    strict: false,
    select: (params) => resolveThreadRouteTarget(params),
  });
  const routeThreadRef = routeTarget?.kind === "server" ? routeTarget.threadRef : null;
  const activeThread = useStore(
    useMemo(() => createThreadSelectorByRef(routeThreadRef), [routeThreadRef]),
  );
  const getDraftThread = useComposerDraftStore((store) => store.getDraftThread);
  const activeDraftThread = useComposerDraftStore(() =>
    routeTarget
      ? routeTarget.kind === "server"
        ? getDraftThread(routeTarget.threadRef)
        : useComposerDraftStore.getState().getDraftSession(routeTarget.draftId)
      : null,
  );
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const defaultThreadRuntime = useSettings((settings) => settings.defaultThreadRuntime);
  const projects = useProjectsAcrossEnvironments();
  const orderedProjects = useMemo(() => {
    return orderItemsByPreferredIds({
      items: projects,
      preferredIds: projectOrder,
      getId: (project) => scopedProjectKey(scopeProjectRef(project.environmentId, project.id)),
    });
  }, [projectOrder, projects]);
  const openNewThread = useNewThreadState();
  const handleNewThread = useCallback(
    async (...args: Parameters<typeof openNewThread>) => {
      await openNewThread(...args);
    },
    [openNewThread],
  );
  const defaultProjectRef = useMemo(() => {
    const project = orderedProjects[0];
    if (!project) return null;
    return resolveNewThreadRuntimeProjectRef({
      requestedRuntime: defaultThreadRuntime,
      primaryEnvironmentId,
      projects,
      projectRef: scopeProjectRef(project.environmentId, project.id),
    });
  }, [defaultThreadRuntime, orderedProjects, primaryEnvironmentId, projects]);

  return {
    activeComposerTarget: composerTargetForRoute(routeTarget),
    activeDraftThread,
    activeThread,
    defaultProjectRef,
    handleNewThread,
    openNewThread,
    routeThreadRef,
  };
}

function composerTargetForRoute(target: ReturnType<typeof resolveThreadRouteTarget> | null) {
  if (target === null) return null;
  return target.kind === "server" ? target.threadRef : target.draftId;
}

/**
 * The draft behind whatever the router is currently showing.
 *
 * A server thread and a draft route both have a draft, but they are looked up
 * differently — by thread ref and by draft id — and neither lookup accepts the
 * other's key. Nothing showing means no draft rather than an error: the router
 * has no match during the first render of a cold start.
 */
function readDraftForRouteTarget(
  target: ReturnType<typeof resolveThreadRouteTarget> | null,
  lookups: {
    readonly getDraftThread: (ref: never) => DraftThreadState | null;
    readonly getDraftSession: (draftId: never) => DraftThreadState | null;
  },
): DraftThreadState | null {
  if (!target) return null;
  return target.kind === "server"
    ? lookups.getDraftThread(target.threadRef as never)
    : lookups.getDraftSession(target.draftId as never);
}
